import { useNavigate } from 'react-router';
import FileSaver from 'file-saver';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    Modal,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    ModalFooter,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
    Select,
    chakra,
} from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { useRef, useState } from 'react';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';
import { TKeywordsByRetailerUploadError } from '../types';
import { useLazyExportKeywordsByRetailersQuery, useUploadRetailersMutation } from 'core/API/keywordsApi';

const KeywordsByRetailersUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadKewordByRetailers, { isLoading }] = useUploadRetailersMutation({
        fixedCacheKey: 'keyword-by-retailers-upload-key',
    });
    const [exportKeywordsByRetailers, { isLoading: isExportLoading, isFetching }] =
        useLazyExportKeywordsByRetailersQuery();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const translate = useTranslation();
    const goBack = () => {
        navigate(-1);
    };
    const toast = useToast();
    const overwriteInitialRef = useRef(null);
    const [modalType, setModalType] = useState<'overwrite' | 'template_choice' | 'error' | 'not_found_retailers'>();
    const [retailerTemplateType, setRetailerTemplateType] = useState<'retailers' | 'retailer_weights'>('retailers');
    const [fileType, setFileType] = useState<'retailers' | 'retailer_weights'>('retailers');
    const [errorBlob, setErrorBlob] = useState<ArrayBuffer | null>(null);
    const [faultyRowCount, setFaultyRowCount] = useState<number>(0);
    const [notFoundRetailers, setNotFoundRetailers] = useState<string[]>([]);

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const handleExportKeywordsByRetailer = async (withWeights = false) => {
        try {
            if (!isExportLoading && !isFetching) {
                const { data: res, isError: isExportError } = await exportKeywordsByRetailers({
                    isTemplate: false,
                    withWeights,
                });
                if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                const fileName = withWeights
                    ? (translate('keywords_by_retailers.export.file_name_weights') as string)
                    : (translate('keywords_by_retailers.export.file_name') as string);
                FileSaver.saveAs(blob, fileName);
            }
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        const handleUploadKeywords = async () => {
            try {
                const bodyFormData = new FormData();
                bodyFormData.append('excelFile', uploadedFile as File);
                bodyFormData.append('uploadMethod', 'overwrite');
                if (fileType === 'retailer_weights') {
                    bodyFormData.append('withWeights', 'true');
                }
                const res = (await uploadKewordByRetailers(bodyFormData)) as TKeywordsByRetailerUploadError;
                if (res.error) {
                    const errorData: NonNullable<TKeywordsByRetailerUploadError['error']>['data'] =
                        res.error.data instanceof ArrayBuffer && res.error.headers?.['content-type']?.includes('json')
                            ? JSON.parse(new TextDecoder().decode(res.error.data))
                            : res.error.data;
                    if (errorData instanceof ArrayBuffer) {
                        setErrorBlob(errorData);
                        setFaultyRowCount(Number(res.error?.headers?.['x-faulty-row-count']) || 0);
                        setModalType('error');
                        onOpen();
                        return;
                    }
                    const errorType = errorData.type;
                    if (errorType === 'missing_headers') {
                        const missingHeaders = errorData.missing_headers;

                        if (missingHeaders?.length)
                            throw new Error(
                                translate('keywords_by_retailers.error.missing_specific_headers', {
                                    headers: missingHeaders.join(', '),
                                }) as string,
                            );
                        else throw new Error(translate('keywords_by_retailers.error.missing_headers') as string);
                    }
                    if (errorType === 'duplicate_keywords') {
                        const duplicateKeywords = errorData.duplicate_keywords as { [key: string]: number };
                        const duplicateKeywordsList = Object.entries(duplicateKeywords)
                            .map(([keyword]) => keyword)
                            .join(', ');
                        throw new Error(
                            `${translate(
                                'keywords_by_retailers.error.conflicting_retailers',
                            )}: ${duplicateKeywordsList}`,
                        );
                    }
                    if (errorType === 'not_found_retailers') {
                        setNotFoundRetailers(errorData.not_found_retailers ?? []);
                        setModalType('not_found_retailers');
                        onOpen();
                        return;
                    }
                    throw new Error(translate('error.generic') as string);
                } else {
                    goBack();
                }
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDownloadErrorFile = () => {
            if (!errorBlob) return;
            const blob = new Blob([errorBlob], { type: EXCEL_FILE_TYPE });
            const errorFileName =
                fileType === 'retailer_weights'
                    ? (translate('keywords_by_retailers.export.error_file_name_weights') as string)
                    : (translate('keywords_by_retailers.export.error_file_name') as string);
            FileSaver.saveAs(blob, errorFileName);
        };

        const renderModal = () => {
            const title =
                modalType === 'template_choice'
                    ? translate('keywords_by_retailers.upload.selectModal.header')
                    : modalType === 'not_found_retailers'
                    ? translate('keywords_by_retailers.upload.missing_retailers.title')
                    : modalType === 'error'
                    ? translate('categories_keywords.upload.modal.error.title')
                    : translate('keyword.upload.modal.overwriteHeader');

            const bodyContent =
                modalType === 'template_choice' ? (
                    <Box>
                        <Text mb={7} color="gray.700" fontStyle={'italic'}>
                            {translate('keywords_by_retailers.upload.selectModal.description')}
                        </Text>
                        <Text>{translate('keywords_by_retailers.upload.retailer.type')}</Text>
                        <Box my={3} mb={5}>
                            <Select
                                bg="white"
                                value={retailerTemplateType}
                                onChange={(e) => {
                                    setRetailerTemplateType(e.target.value as 'retailers' | 'retailer_weights');
                                }}
                                borderColor="gray.200"
                                borderRadius={6}
                                size="md"
                            >
                                <option value="retailers">
                                    {translate('keywords_by_retailers.export.template.retailers')}
                                </option>
                                <option value="retailer_weights">
                                    {translate('keywords_by_retailers.export.template.retailers_weights')}
                                </option>
                            </Select>
                        </Box>
                    </Box>
                ) : modalType === 'not_found_retailers' ? (
                    <Box>
                        <Text mb={4}>{translate('keywords_by_retailers.upload.missing_retailers.description')}</Text>
                        <Box as="ul" pl={5} maxH="40vh" overflowY="auto">
                            {notFoundRetailers.map((retailer, index) => (
                                <Box as="li" key={`${retailer}-${index}`} overflowWrap="anywhere" mb={1}>
                                    {retailer}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : modalType === 'error' ? (
                    <Text>
                        {translate('page.keyword.errorsUploadedDescription', {
                            nb: faultyRowCount,
                            downloadComp: (
                                <chakra.span onClick={handleDownloadErrorFile} {...linkStyle}>
                                    {translate('page.keyword.errorDownload')}
                                </chakra.span>
                            ),
                        })}
                    </Text>
                ) : (
                    <>
                        <Text>{translate('keywords_by_retailer.upload.modal.overwriteDesc')}</Text>
                        <Text>{translate('keywords_by_retailer.upload.modal.overwriteDesc2')}</Text>
                    </>
                );

            const submitButton =
                modalType === 'template_choice' ? (
                    <Button
                        onClick={() => {
                            handleExportKeywordsByRetailer(retailerTemplateType === 'retailer_weights');
                            onClose();
                        }}
                        isLoading={isExportLoading || isFetching}
                        colorScheme="blue"
                        size="sm"
                    >
                        {translate('keywords_by_retailers.upload.selectModal.download')}
                    </Button>
                ) : modalType === 'error' || modalType === 'not_found_retailers' ? (
                    <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                        {translate('keyword.upload.modal.cancel')}
                    </Button>
                ) : (
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(handleUploadKeywords)}
                        isLoading={isLoading}
                        colorScheme="red"
                        size="sm"
                    >
                        {translate('keyword.upload.modal.overwrite')}
                    </Button>
                );

            return (
                <Modal isCentered initialFocusRef={overwriteInitialRef} isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader tabIndex={-1} ref={overwriteInitialRef}>
                            <Text as="h3" color={'gray.900'}>
                                {title}
                            </Text>
                        </ModalHeader>
                        <ModalCloseButton mt="2" isDisabled={isLoading} />
                        <ModalBody fontSize="sm">{bodyContent}</ModalBody>

                        <ModalFooter>
                            {modalType !== 'error' && modalType !== 'not_found_retailers' && (
                                <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                                    {translate('keyword.upload.modal.cancel')}
                                </Button>
                            )}
                            {submitButton}
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            );
        };

        const handleUploadChecker = async () => {
            setModalType('overwrite');
            onOpen();
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text fontSize="md">
                    {translate('keywords_by_retailers.upload.recommendation', {
                        template: (
                            <Text
                                onClick={() => {
                                    setModalType('template_choice');
                                    onOpen();
                                }}
                                as="span"
                                {...linkStyle}
                            >
                                {translate('keywords_by_retailer.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>

                <Text fontSize="md" pt={4} mb={2}>
                    {translate('keywords_by_retailers.upload.file.type')}
                </Text>
                <Box my={3} mb={2}>
                    <Select
                        bg="white"
                        value={fileType}
                        onChange={(e) => {
                            setFileType(e.target.value as 'retailers' | 'retailer_weights');
                        }}
                        borderColor="gray.200"
                        borderRadius={6}
                        size="md"
                    >
                        <option value="retailers">
                            {translate('keywords_by_retailers.export.template.retailers')}
                        </option>
                        <option value="retailer_weights">
                            {translate('keywords_by_retailers.export.template.retailers_weights')}
                        </option>
                    </Select>
                </Box>
                <Text fontSize="md" pt={4} mb={2}>
                    {translate('keyword.upload.file')}
                </Text>
                <Box py={4}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        isLoading={isLoading}
                        onClick={handleUploadChecker}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('keyword.upload.apply')}
                    </Button>
                </Flex>
                {renderModal()}
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('keywords_by_retailer.upload.title') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('keyword.upload.header')}
                    </Heading>
                </Flex>
                <Flex fontSize="sm" direction="row">
                    {renderDescriptionSection()}
                </Flex>
            </Box>
        </Box>
    );
};

export default KeywordsByRetailersUpload;
